import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  List,
  ListItem,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import axios from "axios";
import { useTranslation } from "react-i18next";

const MeetingNotes = ({ currentRoom }) => {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [editingNote, setEditingNote] = useState(null);
  const [loading, setLoading] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  const { t } = useTranslation();

  useEffect(() => {
    if (currentRoom?.id) {
      fetchNotes();
    } else {
      setNotes([]);
    }
  }, [currentRoom]);

  const fetchNotes = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${apiUrl}opportunities/api/interview-rooms/${currentRoom.id}/notes/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
          withCredentials: true,
          credentials: "include",
        }
      );
      setNotes(response.data);
    } catch (error) {
      console.error("Error fetching notes:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      await axios.post(
        `${apiUrl}opportunities/api/meeting-notes/`,
        {
          interview_room: currentRoom.id,
          content: newNote,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
          withCredentials: true,
          credentials: "include",
        }
      );
      setNewNote("");
      fetchNotes();
    } catch (error) {
      console.error("Error adding note:", error);
    }
  };

  const handleUpdateNote = async (noteId) => {
    try {
      const noteToUpdate = notes.find((note) => note.id === noteId);
      await axios.put(
        `${apiUrl}opportunities/api/meeting-notes/${noteId}/`,
        { content: noteToUpdate.content },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
          withCredentials: true,
          credentials: "include",
        }
      );
      setEditingNote(null);
      fetchNotes();
    } catch (error) {
      console.error("Error updating note:", error);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(
        `${apiUrl}opportunities/api/meeting-notes/${noteId}/`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
          withCredentials: true,
          credentials: "include",
        }
      );
      fetchNotes();
    } catch (error) {
      console.error("Error deleting note:", error);
    }
  };

  const handleNoteChange = (noteId, newContent) => {
    const updatedNotes = notes.map((note) =>
      note.id === noteId ? { ...note, content: newContent } : note
    );
    setNotes(updatedNotes);
  };

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent
        sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column" }}
      >
        <Typography variant="h6" gutterBottom>
          {t("carnivals.meetingNotes")}
        </Typography>

        {/* Add Note Section */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            placeholder={t("carnivals.addNotePlaceholder")}
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            size="small"
            sx={{ mb: 1 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddNote}
            disabled={!newNote.trim()}
            size="small"
          >
            {t("carnivals.addNote")}
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Notes List */}
        <List sx={{ flex: 1, overflow: "auto", px: 0 }}>
          {notes.map((note) => (
            <ListItem
              key={note.id}
              sx={{
                flexDirection: "column",
                alignItems: "stretch",
                bgcolor: "background.paper",
                mb: 1,
                p: 2,
                borderRadius: 1,
                boxShadow: 1,
              }}
            >
              {editingNote === note.id ? (
                <>
                  <TextField
                    fullWidth
                    multiline
                    value={note.content}
                    onChange={(e) => handleNoteChange(note.id, e.target.value)}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => handleUpdateNote(note.id)}
                      color="primary"
                    >
                      <SaveIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setEditingNote(null)}
                      color="default"
                    >
                      <CancelIcon />
                    </IconButton>
                  </Box>
                </>
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                      mb: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {new Date(note.created_at).toLocaleString()}
                    </Typography>
                    <Box>
                      <IconButton
                        size="small"
                        onClick={() => setEditingNote(note.id)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteNote(note.id)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography variant="body2">{note.content}</Typography>
                </>
              )}
            </ListItem>
          ))}
          {notes.length === 0 && !loading && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 2 }}
            >
              {t("carnivals.noNotesYet")}
            </Typography>
          )}
        </List>
      </CardContent>
    </Card>
  );
};

export default MeetingNotes;
